import { cloneDeep } from '../../cloneDeep';
import { LayerType, MatteModeType } from '../types/lottie';
import type { Asset, Lottie, Layer } from '../types/lottie';
import { BasePlugin } from './BasePlugin';

type AssetsMap = {
    [key: string]: Asset;
};

export const LAYER_NAME_SCENE_1 = 'scene_1';
export const LAYER_NAME_SCENE_2 = 'scene_2';

/**
 * Extracts duration data and transitions partials from a Lottie JSON. There are 3 types of partials: color, mask and
 * push. Each one can be used as either and in our out transition.
 */
export class TransitionsPlugin extends BasePlugin {
    #assetsMap: AssetsMap = {};

    /**
     * Total duration of animation.
     * This value is in milliseconds.
     */
    transitionDuration = 0;

    /**
     * Duration of time that scenes overlap with each other.
     * This value is in milliseconds.
     */
    overlapDuration = 0;

    /**
     * Difference between the last frame of the animation and start frame of `scene_2`.
     * This value is in milliseconds.
     */
    inTransitionDuration = 0;

    /**
     * Difference between the last frame of `scene_1` and start frame of the animation.
     * This valus is in milliseconds.
     */
    outTransitionDuration = 0;

    /**
     * Amount of timeline to add to `currentTime` of in-transition timeline.
     * This value is in milliseconds.
     *
     * In-transition may or may not start at frame 0. But the in-transition timeline always starts at 0.
     * This value helps us correctly map the current time of timeline with that of the animation.
     *
     * Example scene timeline:
     * |[_ scene timeline _].................|
     * |[_ in-transition timeline _].........|
     *
     * Example animation timeline:
     * |<-- start time -->[_ in-transition _]|
     * |[_ out-transition _____].............|
     */
    inTransitionStartTime = 0;

    /**
     * Amount of time to add to `currentTime` of out-transition timeline.
     * This value is in milliseconds.
     */
    outTransitionStartTime = 0;

    /**
     * Individual transition partials.
     */
    pushInTransitionPartial: Lottie | null = null;
    maskInTransitionPartial: Lottie | null = null;
    colorInTransitionPartial: Lottie | null = null;
    pushOutTransitionPartial: Lottie | null = null;
    maskOutTransitionPartial: Lottie | null = null;
    colorOutTransitionPartial: Lottie | null = null;

    override update(json: Lottie) {
        this.#initAssetsMap(json);
        this.#extractTransitions(json);
    }

    #initAssetsMap(json: Lottie) {
        const assetsMap: AssetsMap = {};
        for (const asset of json.assets) {
            const id = asset.id;
            assetsMap[id] = asset;
        }
        this.#assetsMap = assetsMap;
    }

    #createTransitionPartial(json: Lottie, layerIndexes: number[]): Lottie {
        const assets: Asset[] = [];
        const layers: Layer[] = [];

        // we extracted layers in reverse.
        // so process layer indexes in reverse too.
        let arrayIndex = layerIndexes.length - 1;
        while (arrayIndex >= 0) {
            const layerIndex = layerIndexes[arrayIndex];
            const layer = json.layers[layerIndex];
            layers.push(layer);
            if (layer.ty === LayerType.Precomposition) {
                const refId = layer.refId;
                const asset = this.#assetsMap[refId];
                assets.push(asset);
            }
            arrayIndex--;
        }

        const partial = { ...json, layers, assets };
        return cloneDeep(partial);
    }

    #sceneHasPushTransition(layer: Layer, width: number, height: number) {
        if (!layer.ks) return false;

        if (layer.ks.o?.a === 1 || layer.ks.o?.k !== 100) {
            return true;
        }

        if (layer.ks.r?.a === 1 || layer.ks.r?.k !== 0) {
            return true;
        }

        if (
            layer.ks.a?.a === 1 ||
            layer.ks.a?.k[0] !== width / 2 ||
            layer.ks.a?.k[1] !== height / 2
        ) {
            return true;
        }

        if (
            layer.ks.p?.a === 1 ||
            layer.ks.p?.k[0] !== width / 2 ||
            layer.ks.p?.k[1] !== height / 2
        ) {
            return true;
        }

        if (layer.ks.s?.a === 1 || layer.ks.s?.k[0] !== 100 || layer.ks.s?.k[1] !== 100) {
            return true;
        }

        return false;
    }

    #fixPushTransitionPartial(json: Lottie) {
        for (const layer of json.layers) {
            delete layer.tt;
        }
        return json;
    }

    #fixMaskTransitionPartial(json: Lottie) {
        for (const layer of json.layers) {
            delete layer.td;
        }
        return json;
    }

    /**
     * A transition Lottie JSON has the following structure:
     * - color transition layer (optional) - can have many layers for color transition
     * - mask transition layer (optional) - linked to scene_2 layer via alpha / inverted alpha matte property on scene_2
     * - scene_2 layer (required)
     * - color transition layer (optional) - can have many layers for color transition
     * - mask transition layer (optional) - linked to scene_1 layer via alpha / inverted alpha matte property on scene_1
     * - scene_1 layer (required)
     *
     * This function has been written considering the worst possible Lottie JSON where:
     * 1. The transition is asymmetric i.e. the in-transition duration != out-transition duration
     * 2. Individual layers can have different start time, end time and duration
     * 3. Duration of a mask / color layer != scene_1 / scene_2 duration
     *
     * So basically, we assume the least and derive the most information from Lottie JSON.
     */
    #extractTransitions(json: Lottie) {
        const frameRate = json.fr;

        const startFrameLottie = json.ip;
        const endFrameLottie = json.op;

        let endFrameScene1 = 0;
        let startFrameScene2 = 0;

        let pushOutTransitionPartial = null;
        let maskOutTransitionPartial = null;
        let colorOutTransitionPartial = null;
        let pushInTransitionPartial = null;
        let maskInTransitionPartial = null;
        let colorInTransitionPartial = null;

        const layers = json.layers;
        let layerIndex = layers.length - 1;

        /**
         * Find scene_1 layer.
         * We ignore all layers below scene_1 because they won't be visible anyway.
         */
        while (layerIndex >= 0) {
            const layer = layers[layerIndex];
            if (layer.nm === LAYER_NAME_SCENE_1) {
                break;
            }
            layerIndex--;
        }

        /**
         * Extract required data from scene_1 data
         */
        endFrameScene1 = layers[layerIndex].op;

        /**
         * Extract push transition data for scene_1 and create animation curves for it.
         */
        if (this.#sceneHasPushTransition(layers[layerIndex], json.w, json.h)) {
            pushOutTransitionPartial = this.#fixPushTransitionPartial(
                this.#createTransitionPartial(json, [layerIndex]),
            );
        }

        /**
         * Extract mask transition data for scene_1.
         * If a mask transition exists for scene_1, the following will ALWAYS be true:
         * - It will be linked to scene_1 layer as an alpha / inverted alpha matte on scene_1 layer.
         * - It will be the very next layer above scene_1 as shown in the Lottie JSON structure above.
         */
        if (
            layers[layerIndex].tt === MatteModeType.Alpha ||
            layers[layerIndex].tt === MatteModeType.InvertedAlpha
        ) {
            layerIndex--;
            // verify if current layer is indeed the alpha / inverted alpha matte target
            if (
                layers[layerIndex].td === MatteModeType.Alpha ||
                layers[layerIndex].td === MatteModeType.InvertedAlpha
            ) {
                maskOutTransitionPartial = this.#fixMaskTransitionPartial(
                    this.#createTransitionPartial(json, [layerIndex]),
                );
            }
        }

        /**
         * Extract color transition data for scene_1.
         * All layers between scene_1 / mask (if exists) and scene_2 are assumed to make up the color transition.
         * We group all these layers together in one partial.
         */
        layerIndex--;
        const colorLayerIndexes1 = [];
        while (layerIndex >= 0) {
            const layer = layers[layerIndex];
            if (layer.nm === LAYER_NAME_SCENE_2) {
                break;
            }
            colorLayerIndexes1.push(layerIndex);
            layerIndex--;
        }
        if (colorLayerIndexes1.length) {
            colorOutTransitionPartial = this.#createTransitionPartial(json, colorLayerIndexes1);
        }

        /**
         * Extract required data from scene_2.
         */
        startFrameScene2 = layers[layerIndex].ip;
        /**
         * Extract push transition data for scene_2 and create animation curves for it.
         */
        if (this.#sceneHasPushTransition(layers[layerIndex], json.w, json.h)) {
            pushInTransitionPartial = this.#fixPushTransitionPartial(
                this.#createTransitionPartial(json, [layerIndex]),
            );
        }

        /**
         * Extract mask transition data for scene_2.
         * If a mask transition exists for scene_2, the following will ALWAYS be true:
         * - It will be linked to scene_2 layer as an alpha / inverted alpha matte on scene_2 layer.
         * - It will be the very next layer above scene_2 as shown in the Lottie JSON structure above.
         */
        if (
            layers[layerIndex].tt === MatteModeType.Alpha ||
            layers[layerIndex].tt === MatteModeType.InvertedAlpha
        ) {
            layerIndex--;
            // verify if current layer is indeed the alpha / inverted alpha matte target
            if (
                layers[layerIndex].td === MatteModeType.Alpha ||
                layers[layerIndex].td === MatteModeType.InvertedAlpha
            ) {
                maskInTransitionPartial = this.#fixMaskTransitionPartial(
                    this.#createTransitionPartial(json, [layerIndex]),
                );
            }
        }

        /**
         * Extract color transition data for scene_2.
         * All remaining layers above scene_2 / mask (if exists) make up the color transition.
         * We group all these layers together in one partial.
         */
        layerIndex--;
        const colorLayerIndexes2 = [];
        while (layerIndex >= 0) {
            colorLayerIndexes2.push(layerIndex);
            layerIndex--;
        }
        if (colorLayerIndexes2.length) {
            colorInTransitionPartial = this.#createTransitionPartial(json, colorLayerIndexes2);
        }

        /**
         * Calculate overlap duration.
         * This is the actual amount of duration that scene_2 overlaps with scene_1.
         * None, color-only transition will have zero overlap.
         * Push, mask, color transition will have non-zero overlap.
         */
        const overlapDurationInSeconds = (endFrameScene1 - startFrameScene2) / frameRate;
        this.overlapDuration = overlapDurationInSeconds * 1000;

        /**
         * Calculate total transition duration.
         */
        const transitionDurationInSeconds = (endFrameLottie - startFrameLottie) / frameRate;
        this.transitionDuration = transitionDurationInSeconds * 1000;

        /**
         * Calculate in/out-transition durations.
         */
        const outTransitionDuration = (endFrameScene1 - startFrameLottie) / frameRate;
        this.outTransitionDuration = outTransitionDuration * 1000;
        const inTransitionDuration = (endFrameLottie - startFrameScene2) / frameRate;
        this.inTransitionDuration = inTransitionDuration * 1000;

        /**
         * Calculate in/out transition duration offset,
         */
        const inTransitionOffset = (startFrameScene2 - startFrameLottie) / frameRate;
        this.inTransitionStartTime = inTransitionOffset * 1000;
        this.outTransitionStartTime = 0; // always 0

        /**
         * Update extracted partials.
         */
        this.pushOutTransitionPartial = pushOutTransitionPartial;
        this.maskOutTransitionPartial = maskOutTransitionPartial;
        this.colorOutTransitionPartial = colorOutTransitionPartial;
        this.pushInTransitionPartial = pushInTransitionPartial;
        this.maskInTransitionPartial = maskInTransitionPartial;
        this.colorInTransitionPartial = colorInTransitionPartial;
    }
}
